import { useTranslation } from "react-i18next";
import { getCountriesSorted } from "../lib/countries";
import {
  Select,
  SelectContent,
  SelectEmpty,
  SelectItem,
  SelectSearchInput,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useSelectFilter } from "./ui/useSelectFilter";

/**
 * The one country dropdown implementation, replacing 7 duplicated native
 * `<select>` blocks across TeamsManager (team + manager nationality),
 * CompetitionsManager, VenuesManager, RefereesManager and PlayersManager —
 * all previously hand-rolling the same `getCountriesSorted(currentLang)`
 * option list (PMA-UI wave, Phase 2c).
 *
 * The stored `value` is always the country's English name (`country.en`,
 * "sent to backend" per `lib/countries.ts`) — unchanged from the native
 * `<select>` behavior every call site already relied on. The displayed
 * label is localized (`country[i18n.language]`).
 *
 * ~195 options — always shows the search input (Component API guidance:
 * "only for long lists"; this is the longest list in the app after the
 * 31 competition stages).
 *
 * Placeholder text is a required prop, not hardcoded here, because call
 * sites use different i18n namespaces for it (`common:common.selectCountry`
 * vs. each page's own `admin`-namespace `selectCountry` key) — this stays a
 * dumb, namespace-agnostic presentation component.
 */

interface CountrySelectProps {
  /** The country's English name (`country.en`), or "" for unset. */
  value: string;
  /** Receives the country's English name, or "" if cleared. */
  onChange: (countryEn: string) => void;
  placeholder: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  /** Threaded through to preserve each form's existing validate-on-blur
   *  behavior (`validateAndSetFieldError` at the call site) — the Radix
   *  trigger is a real, focusable button, so a blur still fires the same
   *  way a native `<select>`'s did. */
  onBlur?: () => void;
}

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

export default function CountrySelect({
  value,
  onChange,
  placeholder,
  id,
  required,
  disabled,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
  onBlur,
}: CountrySelectProps) {
  const { t, i18n } = useTranslation("admin");
  const currentLang = i18n.language === "es" ? "es" : "en";

  const options = getCountriesSorted(currentLang).map((country) => ({
    value: country.en,
    label: country[currentLang],
  }));

  // Passed explicitly to SelectValue rather than relying on Radix's own
  // selected-item-text mirroring — see the Component API note: Radix only
  // learns an item's label once that SelectItem has mounted (i.e. after
  // the popup has opened once), which would paint a blank trigger on first
  // render for every edit form (the common case here — editing an existing
  // team/venue/player/referee/competition with a country already set).
  const selectedLabel = options.find((opt) => opt.value === value)?.label;

  const { query, setQuery, visible } = useSelectFilter(options);

  return (
    <Select
      value={value === "" ? UNSET : value}
      onValueChange={(next) => onChange(next === UNSET ? "" : next)}
      disabled={disabled}
      required={required}
    >
      <SelectTrigger
        id={id}
        className={className}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        onBlur={onBlur}
      >
        <SelectValue placeholder={placeholder}>{selectedLabel}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNSET} data-testid="country-option-unset">
          {placeholder}
        </SelectItem>
        <SelectSearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("search")}
        />
        {visible.length === 0 && (
          <SelectEmpty>{t("countrySelectNoMatches")}</SelectEmpty>
        )}
        {visible.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            data-testid={`country-option-${opt.value}`}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
